/**
 * The Book (slice 8b): the play sheet, a native `<dialog>`. Four tabs: the playbook (slice 8c: a
 * tile per play with its diagram, name, a plain-English blurb, and the situations small), the
 * matchup board with SUB and MOVE per row and the bench under it, the move spends (a 2 x 2 grid of
 * type-colored tiles for everyone on the field, PP left/max, and a football line under each; slice
 * 8d: anyone with a battle can spend, the slots the play leans on first with KEY TO THIS PLAY,
 * slots with no battle greyed), and the bag (items and Rest). Every
 * tap edits the Draft and re-reads it through the sim (`preview`, `boardRows`, `subOptions`,
 * `spendSlots`); nothing is sent until Snap. Closing keeps the draft for this call. Slice 11c: a
 * call starts with no play, so the Book opens on Plays and Matchups, Moves, Bag and Snap wait for
 * one ("Pick a play first"); the footer then names the play.
 */
import { DEFAULT_PLAYBOOK, hasWindow, type Game, type Playbook } from '@tdmons/sim';
import { benchViews, boardRows, moveOptions, subOptions } from '../game/board';
import {
  bagItems,
  bagTargets,
  draftSummary,
  hasPlay,
  itemName,
  PICK_A_PLAY,
  preview,
  restOptions,
  type RestOption,
  spendSlots,
  swapsOf,
  windowOf,
  withBag,
  withMove,
  withPlay,
  withRest,
  withSub,
  withoutBag,
  withoutRest,
  withoutSub,
  withoutSwap,
  withSwap,
  type BagItem,
  type Draft,
  type MonView,
  type Slot,
} from '../game/draft';
import type { TheirChanges } from '../game/aiChanges';
import { renderBoard, statusChip, typeChip } from './matchupBoard';
import { playDiagram } from './playDiagram';
import { LIGHT_TYPES } from './types';

export type BookTab = 'plays' | 'board' | 'moves' | 'bag';
const TABS: readonly BookTab[] = ['plays', 'board', 'moves', 'bag'];

export interface BookCall {
  /** The game as the player sees it at this call. */
  game: Readonly<Game>;
  draft: Draft;
  /** "2ND & 3 · OWN 32". */
  situation: string;
  /** Hurry is on the action row (offense): the footer says what it drops. */
  canHurry: boolean;
  /** The other side's changes this call, tagged on its rows (slice 11c). */
  theirs?: TheirChanges;
}

export interface BookHandlers {
  /** The draft changed (the field and the sheet's board follow it). */
  change(d: Draft): void;
  snap(d: Draft): void;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

const button = (cls: string, text: string, onClick: () => void): HTMLButtonElement => {
  const b = el('button', `btn ${cls}`, text);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
};

const pct = (x: number) => `${Math.round(x * 100)}%`;

/** A Rest button's second line: what it does, or why the sim would refuse it (slice 11b). */
const restWhat = (r: RestOption): string =>
  r.disabled === 'sitting'
    ? `SITTING ${r.sitLeft ?? 0}`
    : r.disabled
      ? r.disabled.toUpperCase()
      : r.sitPlays > 0
        ? `${r.moveName} · SITS ${r.sitPlays}`
        : r.moveName;

export class Book {
  private call: BookCall | null = null;
  private tab: BookTab = 'plays';
  private openSub: Slot | null = null;
  private openList: 'sub' | 'move' = 'sub';
  private item: string | null = null;
  private readonly panels: Record<BookTab, HTMLElement>;
  private readonly tabs: Record<BookTab, HTMLButtonElement>;
  private readonly situation: HTMLElement;
  private readonly summary: HTMLElement;
  private readonly hurryNote: HTMLElement;
  private readonly lockNote: HTMLElement;
  private readonly snapButton: HTMLButtonElement;

  constructor(
    private readonly dialog: HTMLDialogElement,
    private readonly on: BookHandlers,
    private readonly book: Playbook = DEFAULT_PLAYBOOK,
  ) {
    const part = <T extends HTMLElement>(sel: string) => {
      const e = dialog.querySelector<T>(sel);
      if (!e) throw new Error(`the Book needs ${sel}`);
      return e;
    };
    this.situation = part('.book-situation');
    this.summary = part('.book-summary');
    this.hurryNote = part('.book-hurry');
    this.lockNote = part('.book-lock');
    this.lockNote.textContent = PICK_A_PLAY;
    this.snapButton = part<HTMLButtonElement>('.book-snap');
    this.panels = Object.fromEntries(TABS.map((t) => [t, part(`#book-${t}`)])) as Record<
      BookTab,
      HTMLElement
    >;
    this.tabs = Object.fromEntries(
      TABS.map((t) => [t, part<HTMLButtonElement>(`#book-tab-${t}`)]),
    ) as Record<BookTab, HTMLButtonElement>;
    for (const t of TABS) this.tabs[t].addEventListener('click', () => this.select(t));
    part('.book-tabs').addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k !== 'ArrowLeft' && k !== 'ArrowRight') return;
      const open = TABS.filter((t) => !this.tabs[t].disabled);
      const i = open.indexOf(this.tab);
      const next = open[(i + (k === 'ArrowRight' ? 1 : -1) + open.length) % open.length] as BookTab;
      this.select(next);
      this.tabs[next].focus();
    });
    part('.book-close').addEventListener('click', () => this.close());
    part('.book-done').addEventListener('click', () => this.close());
    part('.book-snap').addEventListener('click', () => {
      const d = this.call?.draft;
      this.close();
      if (d) this.on.snap(d);
    });
  }

  get isOpen(): boolean {
    return this.dialog.open;
  }

  /** The draft as the Book has it (the last one opened or edited). */
  get draft(): Draft | null {
    return this.call?.draft ?? null;
  }

  /**
   * Open on `tab`, or on the last tab shown; on Plays while the call has no play (slice 11c:
   * every call starts with none, so a new call opens on Plays).
   */
  open(call: BookCall, tab: BookTab = this.tab): void {
    this.call = call;
    this.openSub = null;
    this.item = null;
    this.select(hasPlay(call.draft) ? tab : 'plays');
    if (!this.dialog.open) this.dialog.showModal();
  }

  close(): void {
    if (this.dialog.open) this.dialog.close();
  }

  select(tab: BookTab): void {
    // Matchups, Moves and Bag wait for the play (slice 11c).
    if (tab !== 'plays' && this.call && !hasPlay(this.call.draft)) return;
    this.tab = tab;
    for (const t of TABS) {
      const on = t === tab;
      this.tabs[t].setAttribute('aria-selected', String(on));
      this.tabs[t].tabIndex = on ? 0 : -1;
      this.panels[t].hidden = !on;
    }
    this.render();
  }

  private edit(d: Draft): void {
    if (!this.call) return;
    this.call = { ...this.call, draft: d };
    this.on.change(d);
    this.render();
  }

  private render(): void {
    const call = this.call;
    if (!call) return;
    const pv = preview(call.game, call.draft);
    this.situation.textContent = call.situation;
    this.summary.textContent = draftSummary(call.draft, this.book);
    const locked = !hasPlay(call.draft);
    for (const t of TABS) {
      if (t === 'plays') continue;
      this.tabs[t].disabled = locked;
      if (locked) this.tabs[t].setAttribute('aria-description', PICK_A_PLAY);
      else this.tabs[t].removeAttribute('aria-description');
    }
    this.lockNote.hidden = !locked;
    this.snapButton.disabled = locked;
    this.hurryNote.hidden = !call.canHurry || !hasWindow(windowOf(call.draft));
    const panel = this.panels[this.tab];
    if (this.tab === 'plays') panel.replaceChildren(this.plays(call));
    else if (this.tab === 'board') this.board(panel, call, pv);
    else if (this.tab === 'moves') panel.replaceChildren(...this.moves(call, pv));
    else panel.replaceChildren(...this.bag(call, pv));
  }

  private plays(call: BookCall): HTMLElement {
    const grid = el('div', 'play-grid');
    const plays = call.draft.side === 'offense' ? this.book.offense : this.book.defense;
    for (const p of plays) {
      const b = el('button', 'play-tile');
      b.type = 'button';
      b.setAttribute('aria-pressed', String(p.id === call.draft.playId));
      // The diagram is the tile (slice 8c); the situations and risk are a small line under it.
      const sits = el('span', 'play-sits', [...p.situations, `${p.risk} risk`].join(' · '));
      b.append(
        playDiagram(p),
        el('span', 'play-name', p.name),
        el('span', 'play-blurb', p.blurb),
        sits,
      );
      b.addEventListener('click', () => this.edit(withPlay(call.draft, p.id, this.book)));
      grid.append(b);
    }
    return grid;
  }

  private board(panel: HTMLElement, call: BookCall, pv: Game): void {
    const team = call.game.teams[call.draft.team];
    const chart = (call.draft.side === 'offense' ? team.offense : team.defense) as Record<
      Slot,
      number
    >;
    const drafted = new Map<Slot, string>(
      call.draft.subs
        .filter((s) => s.swap === undefined)
        .map((s) => [s.slot, team.roster[chart[s.slot]]?.name ?? s.slot]),
    );
    const moved = swapsOf(call.draft);
    // A slot already in a drafted sub or MOVE is not offered as a MOVE partner.
    const busy = new Set<Slot>([...drafted.keys(), ...moved.keys()]);
    const toggle = (slot: Slot, list: 'sub' | 'move') => {
      const same = this.openSub === slot && this.openList === list;
      this.openSub = same ? null : slot;
      this.openList = list;
      this.render();
    };
    const root = el('div', 'board');
    renderBoard(
      root,
      boardRows(pv, call.draft.team),
      {
        options: (slot) => subOptions(pv, call.draft.team, slot, call.draft.rest),
        open: this.openSub,
        openList: this.openList,
        drafted,
        toggle: (slot) => toggle(slot, 'sub'),
        pick: (slot, index) => {
          this.openSub = null;
          this.edit(withSub(call.draft, slot, index));
        },
        keep: (slot) => {
          this.openSub = null;
          this.edit(withoutSub(call.game, call.draft, slot));
        },
        moves: (slot) => moveOptions(pv, call.draft.team, slot, busy),
        moved,
        toggleMove: (slot) => toggle(slot, 'move'),
        swap: (slot, other) => {
          this.openSub = null;
          this.edit(withSwap(call.game, call.draft, slot, other));
        },
        unswap: (slot) => {
          this.openSub = null;
          this.edit(withoutSwap(call.game, call.draft, slot));
        },
      },
      benchViews(pv, call.draft.team),
      call.theirs,
    );
    const notes =
      call.draft.side === 'offense'
        ? [el('p', 'book-note', 'Their subs come after your Snap.')]
        : [];
    panel.replaceChildren(root, ...notes);
  }

  private moves(call: BookCall, pv: Game): HTMLElement[] {
    const rows = boardRows(pv, call.draft.team);
    const slots = spendSlots(pv, call.draft, this.book);
    return slots.map((s) => {
      const block = el('section', s.blocked ? 'spend blocked' : 'spend');
      block.dataset.slot = s.slot;
      const row = rows.find((r) => r.offense.slot === s.slot || r.defense.slot === s.slot);
      const them = row ? (row.offense.slot === s.slot ? row.defense : row.offense) : undefined;
      const head = el('p', 'spend-head');
      head.append(
        el('span', 'board-slot', s.slot),
        el('span', 'spend-name', s.name),
        ...s.types.map(typeChip),
      );
      if (row && them) head.append(el('span', 'spend-vs', `vs ${them.name} ${pct(row.chance)}`));
      block.append(head);
      if (s.key) block.append(el('p', 'key-tag', 'KEY TO THIS PLAY'));
      if (s.blocked) block.append(el('p', 'spend-blocked', s.blocked));
      const grid = el('div', 'move-grid');
      for (const t of s.tiles) {
        const b = el('button', LIGHT_TYPES.has(t.type) ? 'move-tile light' : 'move-tile');
        b.type = 'button';
        b.style.background = `var(--type-${t.type.toLowerCase()})`;
        const chosen = call.draft.moves[s.slot] === t.moveId;
        b.setAttribute('aria-pressed', String(chosen));
        b.disabled = !!s.blocked || !!t.disabled;
        b.append(
          el('span', 'move-name', t.name),
          el('span', 'move-effect', t.disabled ? t.disabled.toUpperCase() : t.effect),
          el('span', 'move-pp', `PP ${t.left}/${t.max}`),
        );
        b.setAttribute(
          'aria-label',
          `${t.name}, ${t.type}, ${t.effect}, PP ${t.left} of ${t.max} left${t.disabled ? `, ${t.disabled}` : ''}`,
        );
        b.addEventListener('click', () =>
          this.edit(withMove(call.draft, s.slot, chosen ? null : t.moveId)),
        );
        // What the move does here, under the tile (slice 8d).
        const line = el('p', 'move-line', t.line);
        line.id = `move-line-${s.slot}-${t.moveId}`;
        b.setAttribute('aria-describedby', line.id);
        const cell = el('div', 'move-cell');
        cell.append(b, line);
        grid.append(cell);
      }
      block.append(grid);
      return block;
    });
  }

  private bag(call: BookCall, pv: Game): HTMLElement[] {
    const d = call.draft;
    const out: HTMLElement[] = [el('p', 'book-sub', 'BAG')];
    const items = bagItems(pv, d);
    const list = el('div', 'bag-items');
    if (items.length === 0) list.append(el('p', 'book-note', 'The bag is empty.'));
    for (const it of items) {
      const b = button('bag-item', `${it.name} x${it.count}`, () => {
        this.item = this.item === it.item ? null : it.item;
        this.render();
      });
      b.append(el('span', 'bag-what', it.kind === 'heal-hp' ? `+${it.hp} HP` : 'CURES STATUS'));
      b.setAttribute('aria-pressed', String(this.item === it.item));
      list.append(b);
    }
    out.push(list);
    const picked = items.find((i) => i.item === this.item);
    if (picked) out.push(this.targets(call, pv, picked));
    if (d.bag.length) {
      const used = el('div', 'bag-used');
      const team = call.game.teams[d.team];
      d.bag.forEach((u, i) => {
        const row = el('p', 'bag-use');
        row.append(
          el('span', '', `${itemName(u.item)} → ${team.roster[u.target]?.name ?? u.target}`),
          button('bag-undo', 'Undo', () => this.edit(withoutBag(d, i))),
        );
        used.append(row);
      });
      out.push(used);
    }
    const rests = restOptions(call.game, d);
    if (rests.length) {
      out.push(el('p', 'book-sub', 'REST ON THE BENCH'));
      const box = el('div', 'bag-targets');
      for (const r of rests) {
        const b = this.monButton(r, () =>
          this.edit(r.drafted ? withoutRest(d, r.index) : withRest(d, r.index)),
        );
        b.append(el('span', 'bag-what', restWhat(r)));
        b.setAttribute('aria-pressed', String(r.drafted));
        b.disabled = !!r.disabled;
        box.append(b);
      }
      out.push(box);
    }
    return out;
  }

  private targets(call: BookCall, pv: Game, item: BagItem): HTMLElement {
    const box = el('div', 'bag-targets');
    box.setAttribute('aria-label', `Use ${item.name} on`);
    for (const t of bagTargets(pv, call.draft, item)) {
      const b = this.monButton(t, () => {
        this.item = null;
        this.edit(withBag(call.draft, { item: item.item, target: t.index }));
      });
      b.append(
        el(
          'span',
          'bag-what',
          t.disabled ? t.disabled.toUpperCase() : t.onField ? 'FIELD' : 'BENCH',
        ),
      );
      b.disabled = !!t.disabled;
      box.append(b);
    }
    return box;
  }

  private monButton(m: MonView, onClick: () => void): HTMLButtonElement {
    const b = button('bag-mon', '', onClick);
    const chips = el('span', 'board-chips');
    chips.append(...m.types.map(typeChip), ...(m.status ? [statusChip(m.status)] : []));
    b.append(el('span', 'sub-name', m.name), chips, el('span', 'sub-hp', `HP ${m.hp}/${m.maxHp}`));
    return b;
  }
}
